import { ApiError } from "@/lib/api";

type TQuote = (key: string, opts?: Record<string, unknown>) => string;

export function formatUploadError(
  err: unknown,
  tQuote: TQuote,
  maxUploadMb?: number | null,
): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    switch (err.code) {
      case "file_too_large":
        return tQuote("upload.errorTooLarge", {
          maxMb: (err.params.max_mb as number) ?? 20,
        });
      // KAN-1511 — týž stav 413, ale z proxy PŘED aplikací. Upload teče
      // přes Vercel funkci (rewrite ``/api/:path*`` ve vercel.json) a ta
      // má vlastní strop těla (~4,2 MB po režii multipartu). Odpověď je
      // plain text ``FUNCTION_PAYLOAD_TOO_LARGE`` bez ``error_code``,
      // takže z ní ``parseApiError`` udělá ``http_<status>`` — a do téhle
      // opravy spadla do ``default`` na „Nahrání selhalo. Zkuste to
      // prosím znovu." U příliš velkého souboru je to rada, která nikdy
      // nepomůže: opakování dopadne vždycky stejně.
      //
      // Číslo bereme od volajícího (deklarovaný ``max_upload_mb`` procesu
      // či tenanta). Proxy svůj strop neposílá, takže když ho volající
      // nezná, radši věta bez čísla než vymyšlená hodnota.
      case "http_413":
        return maxUploadMb
          ? tQuote("upload.errorTooLarge", { maxMb: maxUploadMb })
          : tQuote("upload.errorTooLargeNoLimit");
      case "batch_too_large":
        return tQuote("upload.errorBatchTooLarge", {
          maxMb: (err.params.max_mb as number) ?? 100,
        });
      case "too_many_files":
        return tQuote("upload.errorTooManyFiles", {
          max: (err.params.max as number) ?? 25,
          got: (err.params.got as number) ?? 0,
        });
      case "empty_upload":
        return tQuote("upload.errorEmpty");
      // KAN-995 — the BE sends the slot's ``accepted_file_types`` in
      // ``params.expected``; echoing them turns "not supported" into
      // "export it as this instead". Falls back to the bare sentence
      // when a caller raised the code without the list.
      case "unsupported_file_type": {
        const expected = Array.isArray(err.params.expected)
          ? (err.params.expected as string[])
          : [];
        if (expected.length === 0) return tQuote("upload.errorWrongType");
        return tQuote("upload.errorWrongTypeAccepted", {
          accepted: expected.join(", "),
          primary: expected[0],
        });
      }
      // KAN-1535 — matice dovedností nenašla pro TENHLE díl schopný uzel
      // (``candidate_nodes_for_part`` vrátí prázdno, quotes.py:1392).
      // Soubor je přitom v pořádku a projde i analýzou, takže to není
      // chyba zákazníka — jen ho obchod na svých strojích neumí zařadit.
      // Do téhle opravy padal kód do ``default`` na „Nahrání selhalo.
      // Zkuste to prosím znovu." — opakování ale dopadne vždycky stejně,
      // odpověď je deterministická. Táž třída vady jako ``http_413``.
      //
      // ⚠️ ``params`` nese i ``process_type``, což je SLUG. Do zákaznické
      // hlášky nepatří (guard ``tenant-copy-no-identifiers``), takže se
      // posílá jen ``file_name`` — a nabízí se stejná cesta ven jako
      // u ``part_exceeds_machines``: řekni, že to jde po domluvě.
      case "no_capable_node":
        return tQuote("upload.errorNoCapableNode", {
          file_name: err.params.file_name as string,
        });
      // KAN-1923 — týž případ jako ``no_capable_node`` o patro výš: obchod
      // pro operaci nemá žádný stroj. Hláška do téhle opravy posílala
      // ``process_type`` (SLUG) a oslovovala vlastníka („Přidejte ho ve
      // Výrobní kapacity" — sekce, která v menu ani neexistuje). Zákazník
      // stroj přidat nemůže; dostane cestu ven a pokyn pro vlastníka nese
      // ``uploadErrorAdminHint`` jen přihlášenému adminovi obchodu.
      case "no_machine_for_process":
        return tQuote("upload.errorNoMachineForProcess", {
          file_name: err.params.file_name as string,
        });
      // KAN-736 — operace bez souboru se zakládá přes ``POST
      // /quotes/fileless``; nezapojená v mapě tenanta padá sem.
      // Generická hláška by zákazníkovi řekla „něco se pokazilo" o něčem,
      // co je nastavení obchodu, ne jeho chyba.
      case "operation_not_configured":
        return tQuote("upload.errorOperationNotConfigured");
      case "analysis_no_geometry":
        return tQuote("upload.errorAnalysisNoGeometry", {
          file_name: err.params.file_name as string,
        });
      // KAN-735: the BE distinguishes "worker did not answer usably"
      // (analysis_worker_unreachable) from "worker answered but analysis
      // failed" (analysis_unavailable) — an operator distinction; the
      // customer copy is the same for both.
      // KAN-1535 — ``analyzer_timeout`` posílá API z pěti míst
      // (``services/quotes.py``) s ``params.timeout_s``, ale FE pro něj
      // hlášku nikdy neměl, takže padal do ``default``. Číslo z params
      // stojí za vypsání: říká, proti čemu se měřilo, a dělá z hlášky
      // fakt místo omluvy.
      case "analyzer_timeout":
        return tQuote("upload.errorAnalyzerTimeout", {
          file_name: err.params.file_name as string,
          timeout_s: err.params.timeout_s as number,
        });
      // KAN-1622 — ``analysis_worker_interrupted`` (the worker dropped the
      // request mid-analysis) is an operator distinction too; for the
      // customer it is the same "try again shortly".
      case "analysis_unavailable":
      case "analysis_worker_unreachable":
      case "analysis_worker_interrupted":
        return tQuote("upload.errorAnalysisUnavailable");
      // KAN-1622 — the analysis was stopped because it ran the analysis
      // service out of memory. Deterministic for that file, so no "try again".
      case "analysis_too_complex":
        return tQuote("upload.errorAnalysisTooComplex", {
          file_name: err.params.file_name as string,
        });
      case "order_items_exceed_max":
        return tQuote("upload.errorOrderItemsExceedMax", {
          items_count: (err.params.items_count as number) ?? 0,
          max_items: (err.params.max_items as number) ?? 0,
        });
      case "part_exceeds_machines": {
        // Axis-agnostic: the BE sends whatever part_*_mm / max_*_mm
        // dims the process's machine envelope declares (2 for flat
        // processes, 3 for volumetric ones, 1 for length-only ones).
        // Join what actually arrived so one template serves every
        // process without naming any of them.
        const joinDims = (prefix: string) =>
          Object.entries(err.params)
            .filter(
              ([k, v]) =>
                k.startsWith(prefix) &&
                k.endsWith("_mm") &&
                typeof v === "number",
            )
            .map(([, v]) => v)
            .join("×");
        return tQuote("upload.errorPartExceedsMachines", {
          file_name: err.params.file_name as string,
          part_dims: joinDims("part_"),
          max_dims: joinDims("max_"),
        });
      }
      default:
        return tQuote("upload.errorGeneric");
    }
  }
  return tQuote("upload.errorGeneric");
}

/** Admin-only follow-up to an upload error: what the shop owner should fix
 *  and WHERE (an ``/admin/...`` path without the language prefix). */
export interface UploadErrorAdminHint {
  /** i18n key under ``quote:adminInsight.<key>`` (``what`` + ``cta``). */
  key: string;
  /** Admin route the CTA opens — must exist in ``TenantServiceShell``. */
  href: string;
}

/**
 * KAN-1923 — the half of an upload error that belongs to the shop owner.
 *
 * ``formatUploadError`` speaks to the CUSTOMER and must never tell them to go
 * into an admin (they have none). Errors caused by the shop's own setup get
 * their owner instruction here instead; ``UploadErrorAdminInsight`` renders it
 * only for a signed-in admin of the shop. ``null`` = nothing for the owner.
 */
export function uploadErrorAdminHint(err: unknown): UploadErrorAdminHint | null {
  if (!(err instanceof ApiError)) return null;
  switch (err.code) {
    case "no_machine_for_process":
      return { key: "noMachineForProcess", href: "/admin/machines" };
    case "order_items_exceed_max":
      return { key: "orderItemsExceedMax", href: "/admin/order-rules" };
    default:
      return null;
  }
}
